<!--
통관발생비용 자료 - 일괄과금
------------------------------------
등록된 모든 대기자료를 서버 백그라운드에서 일괄 과금
배치 디스패치 → 폴링 → 완료/실패 표시
-->
<template>
    <!-- 모달 -->
    <div id="modal-charge" uk-modal esc-close="false" bg-close="false">
        <div class="uk-modal-dialog uk-modal-body">
            <button class="uk-modal-close-default" type="button" uk-close></button>

            <!-- 대기화면 -->
            <div v-show="jobState === 'ready'">
                <h3 class="uk-modal-title uk-text-primary">일괄 과금</h3>
                <p class="uk-text-muted">
                    등록된 모든 대기자료를 일괄 과금합니다.<br />
                    제외할 자료가 있으면 먼저 삭제하세요.
                </p>

                <div class="uk-text-center uk-margin-top">
                    <button
                        class="uk-button uk-button-primary uk-button-large"
                        type="button"
                        v-on:click="jobStart()"
                    >
                        <span uk-icon="icon: play; ratio: 1.5"></span>
                        과금시작
                    </button>
                </div>
            </div>

            <!-- 대기중 (Job 큐 등록됨, Worker 픽업 전) -->
            <div v-show="jobState === 'pending'">
                <div class="uk-flex uk-flex-middle uk-flex-center">
                    <div uk-spinner="ratio: 0.8" class="uk-margin-small-right"></div>
                    <span class="uk-text-primary uk-text-large">과금 작업이 대기중입니다...</span>
                </div>
                <p class="uk-text-center uk-text-muted">
                    서버에서 작업을 준비하고 있습니다. ({{ totalCount }}건)
                </p>
                <progress class="uk-progress uk-margin-small-top" :value="processedCount" :max="totalCount || 1"></progress>
            </div>

            <!-- 실행중 -->
            <div v-show="jobState === 'doing'">
                <div class="uk-flex uk-flex-middle uk-flex-center">
                    <div uk-spinner="ratio: 0.8" class="uk-margin-small-right"></div>
                    <span class="uk-text-primary uk-text-large">서버에서 과금처리 중입니다...</span>
                </div>
                <p class="uk-text-center uk-text-muted">
                    브라우저를 닫아도 서버에서 처리가 계속됩니다.
                </p>
                <div class="uk-text-center uk-text-large uk-text-primary uk-margin-small-top">
                    {{ processedCount }} / {{ totalCount }}
                </div>
                <progress class="uk-progress uk-margin-small-top" :value="processedCount" :max="totalCount || 1"></progress>
            </div>

            <!-- 완료 -->
            <div v-show="jobState === 'done'">
                <p class="uk-text-center uk-text-large uk-text-primary">
                    <i class="material-icons uk-text-middle" style="font-size: 80px">done</i><br />
                    완료되었습니다
                </p>

                <!-- 과금 결과 요약 -->
                <div v-if="resultSummary" class="uk-text-center uk-margin-bottom">
                    <div class="uk-text-muted">
                        회원사 <span class="uk-text-bold uk-text-primary">{{ resultSummary.total_users }}</span>개
                        &nbsp;|&nbsp;
                        과금 <span class="uk-text-bold uk-text-primary">{{ resultSummary.deposit_count + resultSummary.card_count }}</span>건
                    </div>
                    <div class="uk-text-small uk-margin-small-top">
                        <span class="uk-text-muted">예치금</span>
                        {{ resultSummary.deposit_count }}건
                        {{ formatNumber(resultSummary.deposit_amount) }}원
                        &nbsp;&nbsp;
                        <span class="uk-text-muted">카드</span>
                        {{ resultSummary.card_count }}건
                        {{ formatNumber(resultSummary.card_amount) }}원
                    </div>
                    <div class="uk-text-small">
                        <span class="uk-text-muted">기본</span>
                        {{ formatNumber(resultSummary.total_base_amount) }}원
                        &nbsp;+&nbsp;
                        <span class="uk-text-muted">수수료</span>
                        {{ formatNumber(resultSummary.total_fee_amount) }}원
                        &nbsp;=&nbsp;
                        <span class="uk-text-bold uk-text-primary">{{ formatNumber(resultSummary.total_amount) }}원</span>
                    </div>
                </div>

                <div class="uk-text-center">
                    <button
                        class="uk-button uk-button-primary"
                        type="button"
                        @click="refreshPage"
                    >
                        <i class="material-icons uk-text-middle" style="font-size: 22px">refresh</i>
                        <span class="uk-text-middle">현재페이지 새로고침</span>
                    </button>
                </div>
            </div>

            <!-- 실패 -->
            <div v-show="jobState === 'failed'">
                <p class="uk-text-center uk-text-large uk-text-danger">
                    <i class="material-icons uk-text-middle" style="font-size: 80px">warning</i><br />
                    과금 작업이 실패했습니다
                </p>
                <div v-if="errorMessage" class="uk-text-center uk-text-muted uk-margin-bottom"
                     style="max-height: 100px; overflow-y: auto; padding: 8px; background: #f8f8f8; border-radius: 4px;">
                    {{ errorMessage }}
                </div>
                <p class="uk-text-center uk-text-muted">
                    일시적인 네트워크 문제로 실패할 수 있습니다.<br />
                    2~3회 반복 시도하세요.<br />
                    반복적으로 실패할 경우, [close] 후 한국으로 문의하세요
                </p>
                <div class="uk-text-center">
                    <button
                        class="uk-button uk-button-primary"
                        type="button"
                        @click="refreshPage"
                    >
                        <i class="material-icons uk-text-middle" style="font-size: 22px">refresh</i>
                        <span class="uk-text-middle">현재페이지 새로고침</span>
                    </button>
                </div>
            </div>

        </div>
    </div>

</template>

<script>
    export default {
        props: {
            // csrf-token
            csrfToken: String,
        },
        data: function () {
            return {
                /**
                 * 현재 작업진행상태
                 *  - ready   : 작업 대기중 (초기 화면)
                 *  - pending : 작업 대기중 (Job 큐 등록됨, Worker 픽업 전)
                 *  - doing   : 실행중 (Worker가 Job을 픽업하여 처리중)
                 *  - done    : 완료
                 *  - failed  : 실패
                 */
                jobState: 'ready',

                // 폴링용 데이터
                progressId: null,
                pollTimer: null,
                totalCount: 0,
                processedCount: 0,
                errorMessage: null,

                // 완료 시 과금 결과 요약
                resultSummary: null,
            };
        },
        methods: {
            // 작업초기화 (모달창이 띄워지는 순간 초기화작업)
            jobInit() {
                this.jobState = 'ready';
                this.progressId = null;
                this.totalCount = 0;
                this.processedCount = 0;
                this.errorMessage = null;
                this.resultSummary = null;

                UIkit.modal($('#modal-charge')).show();
            },

            // 작업시작 → 디스패치 API 1회 호출
            jobStart() {
                // 컨펌
                if (! confirm('등록된 모든 대기자료를 일괄 과금합니다. 진행하시겠습니까?')) {
                    return;
                }

                // 진행상태 → 대기중
                this.jobState = 'pending';

                // 디스패치 API 호출 (서버가 READY 전체 조회)
                let cmp = this;
                let opts = {
                    type: 'post',
                    url: '/admin/customs-cost-inputs/dispatch-charge',
                    data: {
                        '_token': cmp.csrfToken,
                    },
                    success: function (response) {
                        cmp.progressId = response.progress_id;
                        cmp.totalCount = response.total_count || 0;
                        cmp.startPolling();
                    },
                    alert_error_msg: function(msg){
                        cmp.jobState = 'failed';
                        cmp.errorMessage = msg;
                    },
                };
                $.app.ajax.module.default(opts);
            },

            // 폴링 시작
            startPolling() {
                let cmp = this;
                this.pollTimer = setInterval(function() {
                    cmp.pollProgress();
                }, 2000);
            },

            // 폴링 중지
            stopPolling() {
                if (this.pollTimer) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                }
            },

            // 진행현황 폴링
            pollProgress() {
                let cmp = this;
                let opts = {
                    type: 'get',
                    url: '/batch-job-progresses/' + this.progressId,
                    success: function (response) {
                        cmp.totalCount = response.total_count || cmp.totalCount;
                        cmp.processedCount = response.processed_count;

                        // 상태별 처리 (PENDING=1, RUNNING=2, COMPLETED=3, FAILED=9)
                        if (response.status === 1) {
                            cmp.jobState = 'pending';
                        } else if (response.status === 2) {
                            cmp.jobState = 'doing';
                        } else if (response.status === 3) {
                            cmp.stopPolling();
                            cmp.resultSummary = response.result_data ? response.result_data.summary : null;
                            cmp.jobState = 'done';
                        } else if (response.status === 9) {
                            cmp.stopPolling();
                            cmp.jobState = 'failed';
                            cmp.errorMessage = response.error_message;
                        }
                    },
                    alert_error_msg: function(msg){
                        // 폴링 에러 시에는 계속 시도 (네트워크 일시 장애 등)
                    },
                };
                $.app.ajax.module.default(opts);
            },

            // 현재페이지 갱신
            refreshPage() {
                $.app.refresh();
            },

            // 숫자 포맷팅 (천 단위 쉼표)
            formatNumber(value) {
                if (value === null || value === undefined) return '0';
                return Number(value).toLocaleString('ko-KR');
            },
        },
        beforeDestroy() {
            this.stopPolling();
        },
    }
</script>

<style lang="scss" scoped>
</style>
